import type { ComponentType } from "react";
import { AlarmList } from "./components/AlarmList";
import { Compass } from "./components/Compass";
import { DeviceTile } from "./components/DeviceTile";
import { Gauge } from "./components/Gauge";
import { Health } from "./components/Health";
import { Heatmap } from "./components/Heatmap";
import { Level } from "./components/Level";
import { MapWidget } from "./components/MapWidget";
import { BarChart, LineChart } from "./components/HistoryChart";
import { PeriodCompare } from "./components/PeriodCompare";
import { PeriodTotals } from "./components/PeriodTotals";
import { Placeholder } from "./components/Placeholder";
import { ResetCounter } from "./components/ResetCounter";
import { SceneButton } from "./components/SceneButton";
import { Slider } from "./components/Slider";
import { Sparkline } from "./components/Sparkline";
import { StateTimeline } from "./components/StateTimeline";
import { Stats } from "./components/Stats";
import { StatusList } from "./components/StatusList";
import { TextNote } from "./components/TextNote";
import { Toggle } from "./components/Toggle";
import { Value } from "./components/Value";
import { WeatherStation } from "./components/WeatherStation";
import { ValueGrid } from "./components/ValueGrid";
import { YearCompare } from "./components/YearCompare";
import { WIDGET_TYPES, type WidgetType } from "./generated/widget-types";
import { NotYet } from "./components/NotYet";
import type { WidgetProps } from "./types";

/**
 * Widget type → component (WEB-02). schedule and insight_card render NotYet. `image` reads its picture from the API,
 * so the device page and dashboards draw it (features/dashboards/pictures); here it is NotYet too.
 */
export const WIDGETS: Partial<Record<WidgetType, ComponentType<WidgetProps>>> = {
  value: Value,
  value_grid: ValueGrid,
  toggle: Toggle,
  slider: Slider,
  gauge: Gauge,
  line_chart: LineChart,
  bar_chart: BarChart,
  map: MapWidget,
  compass: Compass,
  status_list: StatusList,
  alarm_list: AlarmList,
  scene_button: SceneButton,
  device_tile: DeviceTile,
  level: Level,
  sparkline: Sparkline,
  stats: Stats,
  state_timeline: StateTimeline,
  heatmap: Heatmap,
  health: Health,
  period_totals: PeriodTotals,
  period_compare: PeriodCompare,
  year_compare: YearCompare,
  text: TextNote,
  weather_station: WeatherStation,
  reset_counter: ResetCounter,
};

export function isRegistered(type: string): boolean {
  return Object.hasOwn(WIDGETS, type);
}

const KNOWN: readonly string[] = WIDGET_TYPES;

/**
 * Renders any widget. A type from a newer catalogue gets the "update the app" placeholder (DSH-05); a catalogue type
 * this version does not draw yet says so instead, since updating would not help.
 */
export function WidgetView(props: WidgetProps) {
  const C = isRegistered(props.widget.type) ? WIDGETS[props.widget.type as WidgetType] : undefined;
  if (C) return <C {...props} />;
  return KNOWN.includes(props.widget.type) ? <NotYet {...props} /> : <Placeholder {...props} />;
}
